import React, { useEffect, useMemo, useState } from "react";
import { api, inr, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PageExportBar from "@/components/PageExportBar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  UserCircle, Plus, PencilSimple, LockKey, Receipt, Printer, Trash,
  CheckCircle, HandCoins, MagnifyingGlass, CurrencyInr, IdentificationCard, FilePdf,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { downloadPayslipPdf, printPayslip as libPrintPayslip } from "@/lib/payslip";

const PAY_MODES = [
  { v: "cash", label: "Cash" },
  { v: "upi", label: "UPI" },
  { v: "rtgs_neft", label: "NEFT / RTGS" },
  { v: "cheque", label: "Cheque" },
  { v: "bank", label: "Bank" },
  { v: "other", label: "Other" },
];

const monthLabel = (m) => {
  if (!m || m.length !== 7) return m || "—";
  const [y, mm] = m.split("-");
  const d = new Date(Number(y), Number(mm) - 1, 1);
  return d.toLocaleString("en-GB", { month: "short", year: "numeric" });
};
const todayMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

// ------------------- EMPLOYEES TAB -------------------
function EmployeesTab({ onChange }) {
  const { user, stores } = useAuth();
  const isAdmin = user?.role === "admin";
  const canHr = isAdmin || user?.role === "regional_manager" || user?.role === "hr";
  const storeOptions = (stores || []).filter((s) => s.active !== false && (user?.role !== "regional_manager" || (user?.store_ids || []).includes(s.id)));
  const storeLabel = (id) => { const s = (stores || []).find((x) => x.id === id); return s ? `${s.city} — ${s.name}` : "—"; };
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", store_id: "", designation: "", monthly_salary: "" });
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/salary/employees?include_inactive=true");
      setEmployees(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({ name: "", phone: "", email: "", store_id: "", designation: "", monthly_salary: "" });
    setDlgOpen(true);
  };
  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      name: emp.name || "",
      phone: emp.phone || "",
      email: emp.email || "",
      store_id: emp.store_id || "",
      designation: emp.designation || "",
      monthly_salary: String(emp.monthly_salary || 0),
    });
    setDlgOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (user?.role === "regional_manager" && !form.store_id) { toast.error("Pick the employee's store"); return; }
    try {
      if (editing) {
        const payload = {
          name: form.name.trim(),
          phone: form.phone,
          email: form.email.trim(),
          designation: form.designation,
        };
        if (canHr) payload.store_id = form.store_id;
        if (isAdmin) payload.monthly_salary = Number(form.monthly_salary) || 0;
        await api.patch(`/salary/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/salary/employees", {
          name: form.name.trim(),
          phone: form.phone,
          email: form.email.trim(),
          store_id: form.store_id,
          designation: form.designation,
          monthly_salary: Number(form.monthly_salary) || 0,
          active: true,
        });
        toast.success("Employee added");
      }
      setDlgOpen(false);
      await load();
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const deactivate = async (emp) => {
    if (!window.confirm(`Deactivate ${emp.name}? Past records stay intact; they stop appearing on new months.`)) return;
    try {
      await api.delete(`/salary/employees/${emp.id}`);
      toast.success("Employee deactivated");
      await load();
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const hardDelete = async (emp) => {
    if (!window.confirm(`⚠️ PERMANENTLY delete ${emp.name}?\n\nThis wipes:\n  • the employee record\n  • every salary record for them\n  • every advance\n  • every linked Salary Expense voucher (reversed from accounting)\n\nThere is no undo. Type OK to confirm.`)) return;
    const typed = window.prompt('Type DELETE to confirm hard-delete');
    if (typed !== 'DELETE') { toast.error('Hard-delete cancelled'); return; }
    try {
      const { data } = await api.delete(`/salary/employees/${emp.id}?hard=true`);
      toast.success(`Deleted · ${data.records_deleted} records · ${data.vouchers_reversed} vouchers reversed`);
      await load();
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) =>
      (e.name || "").toLowerCase().includes(q) ||
      (e.phone || "").toLowerCase().includes(q) ||
      (e.designation || "").toLowerCase().includes(q)
    );
  }, [employees, search]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="relative w-64 max-w-full">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee…" className="pl-8 h-9" data-testid="salary-emp-search" />
        </div>
        {canHr && (
          <Button onClick={openAdd} className="h-9" data-testid="salary-emp-add">
            <Plus size={13} className="mr-1.5" /> Add Employee
          </Button>
        )}
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table" data-testid="salary-emp-table">
          <thead>
            <tr>
              <th>Name</th><th>Phone</th><th>Designation</th><th>Store</th>
              <th className="num">Monthly Salary</th><th>Status</th><th className="num"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center text-muted-foreground py-6">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-muted-foreground py-6">No employees {search ? "match that search" : "yet — add your first employee to start payroll"}.</td></tr>
            ) : filtered.map((e) => (
              <tr key={e.id} data-testid={`salary-emp-row-${e.id}`}>
                <td className="font-medium">{e.name}<div className="text-[10.5px] font-mono text-muted-foreground" data-testid={`salary-emp-code-${e.id}`}>{e.employee_code}</div></td>
                <td className="font-mono text-[12.5px]">{e.phone || "—"}</td>
                <td>{e.designation || "—"}</td>
                <td className="text-[12px]" data-testid={`salary-emp-store-${e.id}`}>{storeLabel(e.store_id)}</td>
                <td className="num font-mono">{inr(e.monthly_salary || 0)}</td>
                <td>
                  <span className={`text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${e.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {e.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="num">
                  <div className="flex items-center justify-end gap-1">
                    {canHr && (
                      <Link to={`/hr/employees/${e.id}`} title="Documents & Joining Letter" data-testid={`salary-emp-docs-${e.id}`}
                        className="inline-flex items-center gap-1 h-7 px-2 rounded text-[11px] border border-border hover:bg-muted">
                        <IdentificationCard size={13} /> Documents
                      </Link>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(e)} title="Edit" data-testid={`salary-emp-edit-${e.id}`}>
                      <PencilSimple size={13} />
                    </Button>
                    {isAdmin && e.active && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600" onClick={() => deactivate(e)} title="Deactivate (keeps history)" data-testid={`salary-emp-deact-${e.id}`}>
                        <LockKey size={13} />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => hardDelete(e)} title="PERMANENTLY delete (hard-delete)" data-testid={`salary-emp-del-${e.id}`}>
                        <Trash size={13} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="salary-emp-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Employee" : "Add Employee"}</DialogTitle>
            <DialogDescription>
              {editing && !isAdmin ? "Managers cannot change Monthly Salary — ask an admin." : "Monthly Salary is used as the baseline for every month's payroll calculation."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow">Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="salary-emp-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="salary-emp-phone" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Designation</Label>
              <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} data-testid="salary-emp-designation" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Email (for joining letter)</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="salary-emp-email" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Store {user?.role === "regional_manager" && "*"}</Label>
              <Select value={form.store_id || "__none__"} onValueChange={(v) => setForm({ ...form, store_id: v === "__none__" ? "" : v })} disabled={!canHr}>
                <SelectTrigger data-testid="salary-emp-store"><SelectValue placeholder="No store" /></SelectTrigger>
                <SelectContent>
                  {user?.role !== "regional_manager" && <SelectItem value="__none__">No store (Admin only)</SelectItem>}
                  {storeOptions.map((s) => <SelectItem key={s.id} value={s.id}>{s.city} — {s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow flex items-center gap-2">
                Monthly Salary {!isAdmin && editing ? <LockKey size={12} className="text-amber-600" /> : null}
              </Label>
              <Input
                type="number" step="0.01" min="0"
                value={form.monthly_salary}
                onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })}
                disabled={!isAdmin && !!editing}
                className="font-mono"
                data-testid="salary-emp-monthly"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} data-testid="salary-emp-cancel">Cancel</Button>
            <Button onClick={save} data-testid="salary-emp-save">{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------- MONTHLY SALARY TAB -------------------
function MonthlyTab({ month, setMonth }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [edits, setEdits] = useState({}); // { recId: { working, override? } }
  const [savingRow, setSavingRow] = useState(null);
  const [payDlg, setPayDlg] = useState(null); // { rec }
  const [payForm, setPayForm] = useState({ mode: "cash", reference: "", payment_date: todayISO() });
  const [paying, setPaying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/salary/records?month=${month}`);
      setRows(data || []);
      setEdits({});
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month]);

  const totals = useMemo(() => {
    const sumBy = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
    return {
      earned: sumBy("earned_salary"),
      advance: sumBy("advance_given"),
      final: sumBy("final_payable"),
      pending: rows.filter((r) => r.payment_status !== "paid").reduce((a, r) => a + (Number(r.final_payable) || 0), 0),
      paidCount: rows.filter((r) => r.payment_status === "paid").length,
    };
  }, [rows]);

  const onEdit = (rec, patch) => {
    setEdits((e) => ({ ...e, [rec.id]: { ...e[rec.id], ...patch } }));
  };
  const saveRow = async (rec) => {
    const edit = edits[rec.id] || {};
    const working = edit.working !== undefined ? Number(edit.working) : Number(rec.employee_working_days || 0);
    const override = edit.override !== undefined ? Number(edit.override) : null;
    setSavingRow(rec.id);
    try {
      const payload = {
        employee_id: rec.employee_id,
        month,
        employee_working_days: working,
      };
      if (isAdmin && override !== null && !isNaN(override)) payload.monthly_salary_override = override;
      const { data } = await api.post("/salary/records", payload);
      setRows((rs) => rs.map((r) => (r.id === rec.id ? { ...r, ...data } : r)));
      setEdits((e) => { const n = { ...e }; delete n[rec.id]; return n; });
      toast.success(`Updated ${rec.employee_name}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingRow(null); }
  };

  const openPay = (rec) => {
    setPayDlg({ rec });
    setPayForm({ mode: "cash", reference: "", payment_date: todayISO() });
  };
  const confirmPay = async () => {
    if (!payDlg) return;
    const rec = payDlg.rec;
    if (!window.confirm(`Pay ₹${(rec.final_payable || 0).toFixed(2)} to ${rec.employee_name} for ${monthLabel(rec.month)} via ${PAY_MODES.find((m) => m.v === payForm.mode)?.label || payForm.mode}?\n\nThis creates a locked Salary Expense voucher and cannot be undone from the UI.`)) return;
    setPaying(true);
    try {
      const { data } = await api.post(`/salary/records/${rec.id}/pay`, payForm);
      toast.success(`Paid · Voucher ${data.voucher_number}`);
      setPayDlg(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPaying(false); }
  };

  const openVoucher = () => {};  // removed — Voucher button no longer rendered

  const unpayRecord = async (rec) => {
    if (!window.confirm(`Reverse payment for ${rec.employee_name} (${monthLabel(rec.month)})?\n\nThis deletes the linked Salary Expense voucher ${rec.voucher_number} and flips the record back to Pending. The advance ledger stays intact so you can re-pay with corrected details.`)) return;
    try {
      await api.post(`/salary/records/${rec.id}/unpay`);
      toast.success(`Payment reversed · ${rec.voucher_number} deleted`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteRecord = async (rec) => {
    const extra = rec.payment_status === "paid"
      ? `\n\nThis will also DELETE the linked voucher ${rec.voucher_number} (₹${rec.final_payable.toFixed(2)}) and reverse the accounting entry.`
      : "";
    if (!window.confirm(`Delete salary record for ${rec.employee_name} (${monthLabel(rec.month)})?${extra}\n\nThe row will auto-recreate as blank when the Monthly screen reloads.`)) return;
    try {
      await api.delete(`/salary/records/${rec.id}`);
      toast.success("Record deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const [adminEditRow, setAdminEditRow] = useState(null);
  const [adminEditForm, setAdminEditForm] = useState({});
  const openAdminEdit = (rec) => {
    setAdminEditRow(rec);
    setAdminEditForm({
      monthly_salary: rec.monthly_salary,
      total_working_days: rec.total_working_days,
      employee_working_days: rec.employee_working_days,
      payment_date: rec.payment_date || "",
      payment_mode: rec.payment_mode || "",
      payment_reference: rec.payment_reference || "",
    });
  };
  const saveAdminEdit = async () => {
    if (!adminEditRow) return;
    try {
      const payload = {
        monthly_salary: Number(adminEditForm.monthly_salary) || 0,
        total_working_days: Number(adminEditForm.total_working_days) || 0,
        employee_working_days: Number(adminEditForm.employee_working_days) || 0,
      };
      if (adminEditRow.payment_status === "paid") {
        payload.payment_date = adminEditForm.payment_date;
        payload.payment_mode = adminEditForm.payment_mode;
        payload.payment_reference = adminEditForm.payment_reference;
      }
      await api.patch(`/salary/records/${adminEditRow.id}`, payload);
      toast.success("Record updated — linked voucher synced");
      setAdminEditRow(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const fillFromAttendance = async () => {
    try {
      const { data } = await api.get(`/hrms/attendance-summary?month=${month}`);
      const targets = rows.filter((r) => r.payment_status !== "paid" && data[r.employee_id]);
      if (!targets.length) { toast.info("No pending rows to fill"); return; }
      if (!window.confirm(`Fill Worked Days for ${targets.length} pending employee(s) from attendance + approved paid leave? You can still edit them afterwards.`)) return;
      for (const r of targets) {
        await api.post("/salary/records", { employee_id: r.employee_id, month, employee_working_days: data[r.employee_id].suggested_days });
      }
      toast.success("Worked Days filled from attendance");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const printPayslip = (rec) => libPrintPayslip(rec.id);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1.5">
            <Label className="eyebrow">Month</Label>
            <Input type="month" min="1990-01" value={month} onChange={(e) => setMonth(e.target.value || todayMonth())} className="font-mono h-9 w-44" data-testid="salary-month" />
          </div>
          <div className="text-[11px] font-mono text-muted-foreground pb-2">
            Total Working Days auto-fills from the calendar · {rows[0]?.total_working_days || "—"} days this month
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11.5px] font-mono flex-wrap">
          <span>Earned: <b>{inr(totals.earned)}</b></span>
          <span>Advance: <b>{inr(totals.advance)}</b></span>
          <span>Payable: <b style={{ color: "hsl(var(--primary))" }}>{inr(totals.final)}</b></span>
          <span>Pending: <b style={{ color: "hsl(var(--destructive))" }}>{inr(totals.pending)}</b></span>
          <span>Paid: <b>{totals.paidCount} / {rows.length}</b></span>
          <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={fillFromAttendance} data-testid="salary-fill-attendance">Fill Worked Days from Attendance</Button>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="data-table" data-testid="salary-monthly-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th className="num">Monthly Salary</th>
              <th className="num">Total Days</th>
              <th className="num">Worked Days</th>
              <th className="num">Daily</th>
              <th className="num">Earned</th>
              <th className="num">Advance</th>
              <th className="num">Final Payable</th>
              <th>Status</th>
              <th className="num"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="text-center text-muted-foreground py-6">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={10} className="text-center text-muted-foreground py-6">No active employees — add one in the Employees tab first.</td></tr>
            ) : rows.map((r) => {
              const edit = edits[r.id] || {};
              const dirty = edit.working !== undefined || edit.override !== undefined;
              const paid = r.payment_status === "paid";
              const monthlyShown = edit.override !== undefined ? edit.override : r.monthly_salary;
              return (
                <tr key={r.id} data-testid={`salary-row-${r.id}`}>
                  <td className="font-medium">
                    {r.employee_name}
                    {r.employee_designation ? <div className="text-[11px] text-muted-foreground">{r.employee_designation}</div> : null}
                  </td>
                  <td className="num">
                    {isAdmin && !paid ? (
                      <Input
                        type="number" step="0.01" min="0"
                        value={monthlyShown}
                        onChange={(e) => onEdit(r, { override: e.target.value })}
                        className="h-7 w-28 font-mono text-right"
                        data-testid={`salary-row-salary-${r.id}`}
                      />
                    ) : (
                      <span className="font-mono inline-flex items-center gap-1">
                        {paid ? null : <LockKey size={10} className="text-amber-600" title="Monthly Salary — admin only" />}
                        {inr(r.monthly_salary)}
                      </span>
                    )}
                  </td>
                  <td className="num font-mono text-muted-foreground">{r.total_working_days}</td>
                  <td className="num">
                    {paid ? (
                      <span className="font-mono">{r.employee_working_days}</span>
                    ) : (
                      <Input
                        type="number" step="0.5" min="0" max={r.total_working_days}
                        value={edit.working !== undefined ? edit.working : r.employee_working_days}
                        onChange={(e) => onEdit(r, { working: e.target.value })}
                        className="h-7 w-20 font-mono text-right"
                        data-testid={`salary-row-worked-${r.id}`}
                      />
                    )}
                  </td>
                  <td className="num font-mono text-[12.5px] text-muted-foreground">{inr(r.daily_salary)}</td>
                  <td className="num font-mono">{inr(r.earned_salary)}</td>
                  <td className="num font-mono" style={{ color: r.advance_given > 0 ? "hsl(var(--warning))" : undefined }}>
                    {inr(r.advance_given)}
                  </td>
                  <td className="num font-mono" style={{ color: "hsl(var(--primary))", fontWeight: 600 }}>{inr(r.final_payable)}</td>
                  <td>
                    {paid ? (
                      <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-green-100 text-green-700 inline-flex items-center gap-1">
                        <CheckCircle size={11} weight="fill" /> Paid
                      </span>
                    ) : (
                      <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-700">Pending</span>
                    )}
                  </td>
                  <td className="num">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      {!paid && dirty && (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={savingRow === r.id} onClick={() => saveRow(r)} data-testid={`salary-row-save-${r.id}`}>
                          {savingRow === r.id ? "…" : "Save"}
                        </Button>
                      )}
                      {!paid && !dirty && (
                        <Button size="sm" className="h-7 text-[11px]" onClick={() => openPay(r)} disabled={r.final_payable <= 0} data-testid={`salary-row-pay-${r.id}`} title={r.final_payable <= 0 ? "Nothing to pay" : "Pay salary"}>
                          <HandCoins size={12} className="mr-1" /> Pay
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => printPayslip(r)} data-testid={`salary-row-payslip-${r.id}`} title="Print / download payslip">
                        <Printer size={12} />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => downloadPayslipPdf(r.id)} data-testid={`salary-row-payslip-pdf-${r.id}`} title="Download salary slip PDF">
                        <FilePdf size={13} />
                      </Button>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={() => openAdminEdit(r)} data-testid={`salary-row-admin-edit-${r.id}`} title="Admin edit (overrides every field)">
                          <PencilSimple size={12} />
                        </Button>
                      )}
                      {isAdmin && paid && (
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-amber-700" onClick={() => unpayRecord(r)} data-testid={`salary-row-unpay-${r.id}`} title="Reverse payment (keeps advance, deletes voucher)">
                          ⟲
                        </Button>
                      )}
                      {isAdmin && (
                        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-destructive" onClick={() => deleteRecord(r)} data-testid={`salary-row-delete-${r.id}`} title="Delete this record (reverses voucher if paid)">
                          <Trash size={12} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!payDlg} onOpenChange={(o) => !o && setPayDlg(null)}>
        <DialogContent data-testid="salary-pay-dialog">
          <DialogHeader>
            <DialogTitle>Pay Salary — {payDlg?.rec.employee_name}</DialogTitle>
            <DialogDescription>
              Creates a locked <b>Salary Expense Voucher</b> for ₹{(payDlg?.rec.final_payable || 0).toFixed(2)} and posts the accounting entry (Dr Salary Expense · Cr {PAY_MODES.find((m) => m.v === payForm.mode)?.label || "Cash"}). This cannot be undone from the UI.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5">
              <Label className="eyebrow">Payment Date</Label>
              <Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} className="font-mono" data-testid="salary-pay-date" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Payment Mode</Label>
              <Select value={payForm.mode} onValueChange={(v) => setPayForm({ ...payForm, mode: v })}>
                <SelectTrigger data-testid="salary-pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAY_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow">Reference (UPI ID / UTR / Cheque No)</Label>
              <Input value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className="font-mono" data-testid="salary-pay-ref" />
            </div>
            <div className="md:col-span-2 rounded border border-border bg-muted/30 p-3 text-[12.5px] font-mono">
              Earned <b>{inr(payDlg?.rec.earned_salary || 0)}</b> − Advance <b>{inr(payDlg?.rec.advance_given || 0)}</b> = Final <b style={{ color: "hsl(var(--primary))" }}>{inr(payDlg?.rec.final_payable || 0)}</b>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDlg(null)} disabled={paying} data-testid="salary-pay-cancel">Cancel</Button>
            <Button onClick={confirmPay} disabled={paying} data-testid="salary-pay-confirm">
              <CheckCircle size={13} className="mr-1.5" />{paying ? "Processing…" : "Confirm Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adminEditRow} onOpenChange={(o) => !o && setAdminEditRow(null)}>
        <DialogContent data-testid="salary-admin-edit-dialog">
          <DialogHeader>
            <DialogTitle>Admin Edit — {adminEditRow?.employee_name} · {adminEditRow && monthLabel(adminEditRow.month)}</DialogTitle>
            <DialogDescription>
              Full override. Saving recomputes Daily / Earned / Final and {adminEditRow?.payment_status === "paid" ? "syncs the linked Salary Expense voucher so Cash Book and P&L stay accurate." : "updates the pending record."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5">
              <Label className="eyebrow">Monthly Salary</Label>
              <Input type="number" step="0.01" min="0" value={adminEditForm.monthly_salary ?? ""} onChange={(e) => setAdminEditForm({ ...adminEditForm, monthly_salary: e.target.value })} className="font-mono" data-testid="salary-admin-monthly" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Total Working Days</Label>
              <Input type="number" step="0.5" min="0" value={adminEditForm.total_working_days ?? ""} onChange={(e) => setAdminEditForm({ ...adminEditForm, total_working_days: e.target.value })} className="font-mono" data-testid="salary-admin-total-days" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow">Employee Working Days</Label>
              <Input type="number" step="0.5" min="0" value={adminEditForm.employee_working_days ?? ""} onChange={(e) => setAdminEditForm({ ...adminEditForm, employee_working_days: e.target.value })} className="font-mono" data-testid="salary-admin-worked" />
            </div>
            {adminEditRow?.payment_status === "paid" && (
              <>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Payment Date</Label>
                  <Input type="date" value={adminEditForm.payment_date || ""} onChange={(e) => setAdminEditForm({ ...adminEditForm, payment_date: e.target.value })} className="font-mono" data-testid="salary-admin-paydate" />
                </div>
                <div className="space-y-1.5">
                  <Label className="eyebrow">Payment Mode</Label>
                  <Select value={adminEditForm.payment_mode || "cash"} onValueChange={(v) => setAdminEditForm({ ...adminEditForm, payment_mode: v })}>
                    <SelectTrigger data-testid="salary-admin-paymode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAY_MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="eyebrow">Reference</Label>
                  <Input value={adminEditForm.payment_reference || ""} onChange={(e) => setAdminEditForm({ ...adminEditForm, payment_reference: e.target.value })} className="font-mono" data-testid="salary-admin-ref" />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminEditRow(null)} data-testid="salary-admin-cancel">Cancel</Button>
            <Button onClick={saveAdminEdit} data-testid="salary-admin-save">Save Overrides</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function AdvancesTab({ onChange }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [editing, setEditing] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [list, setList] = useState([]);
  const [filterEmp, setFilterEmp] = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", month: todayMonth(), amount: "", date: todayISO(), notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const emps = await api.get("/salary/employees");
      setEmployees(emps.data || []);
      const q = new URLSearchParams();
      if (filterEmp) q.set("employee_id", filterEmp);
      if (filterMonth) q.set("month", filterMonth);
      const { data } = await api.get(`/salary/advances?${q}`);
      setList(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterEmp, filterMonth]);

  const save = async () => {
    if (!form.employee_id) { toast.error("Pick an employee"); return; }
    if (!(Number(form.amount) > 0)) { toast.error("Amount must be > 0"); return; }
    try {
      if (editing) {
        await api.patch(`/salary/advances/${editing.id}`, {
          amount: Number(form.amount),
          date: form.date,
          notes: form.notes,
          month: form.month,
        });
        toast.success("Advance updated");
      } else {
        await api.post("/salary/advances", {
          employee_id: form.employee_id,
          month: form.month,
          amount: Number(form.amount),
          date: form.date,
          notes: form.notes,
        });
        toast.success("Advance recorded");
      }
      setDlgOpen(false);
      setEditing(null);
      setForm({ employee_id: "", month: todayMonth(), amount: "", date: todayISO(), notes: "" });
      await load();
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      employee_id: a.employee_id,
      month: a.month,
      amount: String(a.amount || ""),
      date: a.date || todayISO(),
      notes: a.notes || "",
    });
    setDlgOpen(true);
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete ${inr(a.amount)} advance from ${a.employee_name} (${monthLabel(a.month)})?`)) return;
    try {
      await api.delete(`/salary/advances/${a.id}`);
      toast.success("Advance deleted");
      await load();
      onChange && onChange();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const totalAdvance = list.reduce((a, r) => a + (Number(r.amount) || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1.5">
            <Label className="eyebrow">Employee</Label>
            <Select value={filterEmp || "all"} onValueChange={(v) => setFilterEmp(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56" data-testid="adv-filter-emp"><SelectValue placeholder="All employees" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">Month</Label>
            <Input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="font-mono h-9 w-40" data-testid="adv-filter-month" />
          </div>
          <div className="text-[11.5px] font-mono text-muted-foreground pb-2">
            {list.length} entry(ies) · Total <b>{inr(totalAdvance)}</b>
          </div>
        </div>
        <Button onClick={() => setDlgOpen(true)} className="h-9" data-testid="adv-add">
          <Plus size={13} className="mr-1.5" /> Add Advance
        </Button>
      </div>

      <div className="card-flat overflow-hidden">
        <table className="data-table" data-testid="adv-table">
          <thead>
            <tr>
              <th>Date</th><th>Employee</th><th>Month</th>
              <th className="num">Amount</th><th>Notes</th><th className="num"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center text-muted-foreground py-6">Loading…</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-muted-foreground py-6">No advances match.</td></tr>
            ) : list.map((a) => (
              <tr key={a.id} data-testid={`adv-row-${a.id}`}>
                <td className="font-mono">{fmtDate(a.date)}</td>
                <td>{a.employee_name}</td>
                <td className="font-mono">{monthLabel(a.month)}</td>
                <td className="num font-mono">{inr(a.amount)}</td>
                <td className="text-[12.5px]">{a.notes || "—"}</td>
                <td className="num">
                  <div className="flex items-center justify-end gap-1">
                    {isAdmin && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(a)} title="Edit advance (admin)" data-testid={`adv-edit-${a.id}`}>
                        <PencilSimple size={13} />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => remove(a)} title={isAdmin ? "Delete advance (admin — allowed even on paid months)" : "Delete advance"} data-testid={`adv-del-${a.id}`}>
                      <Trash size={13} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dlgOpen} onOpenChange={(o) => { setDlgOpen(o); if (!o) setEditing(null); }}>
        <DialogContent data-testid="adv-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Advance" : "Add Advance"}</DialogTitle>
            <DialogDescription>{editing ? "Admin override. Changes resync the matching month's salary record if it is still pending." : "Record a salary advance against an employee for a specific month. Deducted from Final Payable when that month's salary is calculated."}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow">Employee *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })} disabled={!!editing}>
                <SelectTrigger data-testid="adv-form-emp"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Against Month *</Label>
              <Input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="font-mono" data-testid="adv-form-month" />
            </div>
            <div className="space-y-1.5">
              <Label className="eyebrow">Date Paid *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="font-mono" data-testid="adv-form-date" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow">Amount *</Label>
              <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono" data-testid="adv-form-amount" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label className="eyebrow">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="adv-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDlgOpen(false); setEditing(null); }} data-testid="adv-form-cancel">Cancel</Button>
            <Button onClick={save} data-testid="adv-form-save">{editing ? "Save" : "Record Advance"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------- HISTORY TAB -------------------
function HistoryTab() {
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterEmp, setFilterEmp] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const emps = await api.get("/salary/employees?include_inactive=true");
      setEmployees(emps.data || []);
      const q = new URLSearchParams();
      if (filterEmp) q.set("employee_id", filterEmp);
      if (filterYear) q.set("year", filterYear);
      if (filterStatus) q.set("status", filterStatus);
      const { data } = await api.get(`/salary/history?${q}`);
      setRows(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filterEmp, filterYear, filterStatus]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.employee_name || "").toLowerCase().includes(q) ||
      (r.voucher_number || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => now - i);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1.5">
            <Label className="eyebrow">Employee</Label>
            <Select value={filterEmp || "all"} onValueChange={(v) => setFilterEmp(v === "all" ? "" : v)}>
              <SelectTrigger className="w-52" data-testid="hist-filter-emp"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">Year</Label>
            <Select value={filterYear || "all"} onValueChange={(v) => setFilterYear(v === "all" ? "" : v)}>
              <SelectTrigger className="w-28" data-testid="hist-filter-year"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="eyebrow">Status</Label>
            <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="w-36" data-testid="hist-filter-status"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="relative w-56 mt-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / voucher…" className="pl-8 h-9" data-testid="hist-search" />
          </div>
        </div>
        <PageExportBar
          title="Salary History"
          filename={`salary-history-${new Date().toISOString().slice(0, 10)}`}
          columns={[
            { key: "employee_name", label: "Employee" },
            { key: "month", label: "Month" },
            { key: "earned_salary", label: "Earned" },
            { key: "advance_given", label: "Advance" },
            { key: "final_payable", label: "Final Payable" },
            { key: "payment_status", label: "Status" },
            { key: "voucher_number", label: "Voucher" },
            { key: "payment_date", label: "Paid On" },
          ]}
          rows={filtered}
          testidPrefix="hist"
        />
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="data-table" data-testid="hist-table">
          <thead>
            <tr>
              <th>Employee</th><th>Month</th>
              <th className="num">Earned</th><th className="num">Advance</th><th className="num">Final Payable</th>
              <th>Status</th><th>Voucher</th><th>Slip</th><th>Paid On</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center text-muted-foreground py-6">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-muted-foreground py-6">No salary records match these filters.</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} data-testid={`hist-row-${r.id}`}>
                <td className="font-medium">{r.employee_name}</td>
                <td className="font-mono">{monthLabel(r.month)}</td>
                <td className="num font-mono">{inr(r.earned_salary)}</td>
                <td className="num font-mono">{inr(r.advance_given)}</td>
                <td className="num font-mono font-semibold" style={{ color: "hsl(var(--primary))" }}>{inr(r.final_payable)}</td>
                <td>
                  {r.payment_status === "paid" ? (
                    <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-green-100 text-green-700">Paid</span>
                  ) : (
                    <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-700">Pending</span>
                  )}
                </td>
                <td className="font-mono text-[12.5px]">{r.voucher_number || "—"}</td>
                <td>
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px]" onClick={() => downloadPayslipPdf(r.id)} data-testid={`hist-slip-pdf-${r.id}`}>
                    <FilePdf size={13} className="mr-1" />PDF
                  </Button>
                </td>
                <td className="font-mono text-[12.5px]">{r.payment_date ? fmtDate(r.payment_date) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ------------------- MAIN PAGE -------------------
export default function Salary() {
  const [tab, setTab] = useState("monthly");
  const [month, setMonth] = useState(todayMonth());
  const [bump, setBump] = useState(0);

  const tabs = [
    { id: "monthly", label: "Monthly Salary", icon: CurrencyInr },
    { id: "employees", label: "Employees", icon: UserCircle },
    { id: "advances", label: "Advances", icon: HandCoins },
    { id: "history", label: "History", icon: Receipt },
  ];

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow flex items-center gap-1.5"><UserCircle size={11} /> Payroll</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Salary Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Daily-rate payroll with per-month working days, advance tracking, idempotent payments, and auto-posted Salary Expense accounting entries.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-border overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${
              tab === t.id
                ? "border-primary text-primary font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`salary-tab-${t.id}`}
          >
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {tab === "monthly" && <MonthlyTab key={`m-${bump}`} month={month} setMonth={setMonth} />}
      {tab === "employees" && <EmployeesTab key={`e-${bump}`} onChange={() => setBump((n) => n + 1)} />}
      {tab === "advances" && <AdvancesTab key={`a-${bump}`} onChange={() => setBump((n) => n + 1)} />}
      {tab === "history" && <HistoryTab key={`h-${bump}`} />}
    </div>
  );
}
